import { useEffect, useRef, useState } from 'react';
import { useT } from '../../i18n';
import { readBookingPaymentStatus, type PaymentSubject } from '../../lib/api/memberPayment';
import { StripePaymentSheet } from './StripePaymentSheet';

// ═══════════════════════════════════════════════════════════════════
// «BETAL NÅ», WHEREVER AN UNPAID BOOKING IS SHOWN (#284).
//
// THE REASON THIS EXISTS RATHER THAN AN INLINE SHEET: slice 5b opened the payment
// sheet once, straight after booking, and `onCancel` merely closed it. One
// accidental tap therefore left a real `pending` booking holding a court with NO
// way back to the outstanding PaymentIntent — and CLOCK 1's value is still open
// (O5), so the hold is not guaranteed to lapse and free the court either.
//
// «The member can pay later» was a claim in a PR body with nothing behind it. This
// is the later.
//
// So the action lives with the BOOKING, not with the booking flow: the
// confirmation that has just appeared, and the member's own reservations list. Both
// render this, so there is one answer to «is money owed, and how do I pay it».
// ═══════════════════════════════════════════════════════════════════

/**
 * WHETHER A BOOKING STILL OWES MONEY (#284).
 *
 * `pending` is the SERVER's word for «priced and not paid» — `/member/bookings`
 * derives the price from the club's own zones and sets this itself. So no screen's
 * price preview decides whether payment is due, and none can be talked into
 * skipping it: a club whose zones changed between preview and commit charges what
 * the server derived, and a booking the server calls `free` is done.
 *
 * Exported because it is the rule, not a detail — it is what stands between «the
 * member pays for what they booked» and «the browser decided nothing was owed».
 */
export function paymentDueFor<T>(booking: T): booking is T & { id: string } {
  const b = booking as { id?: unknown; paymentStatus?: unknown } | null;
  return (
    b != null &&
    typeof b.id === 'string' &&
    b.id !== '' &&
    // Exactly `pending`. Not «truthy», not «not free» — an unknown status is not a
    // licence to charge somebody.
    b.paymentStatus === 'pending'
  );
}

export interface PayForBookingProps {
  /** The booking as the server returned it. Renders nothing unless it owes money. */
  booking: unknown;
  /**
   * WHAT IS BEING PAID FOR (docs/domains/ASSET_BOOKING.md, slice 2): a court
   * booking — with the equipment riding on it, as one amount — or a standalone
   * equipment booking, which has its own hold and its own payment path. The
   * default is the court, so every existing call site is unchanged.
   */
  subject?: PaymentSubject;
  /**
   * How long the court is still held, in ms, as the SERVER measured it (#607).
   *
   * Passed straight through from the booking response where the screen has it —
   * the confirmation, which has just booked. Where it does not (a reservations
   * list rendered from a projection), it is left out and this component asks for
   * it instead, which is one cheap read per unpaid booking.
   */
  expiresInMs?: number;
  /** Court/date/time, for the sheet's summary. Display only. */
  summary: string;
  /**
   * Open the sheet immediately.
   *
   * True straight after booking, where paying is the obvious next step; false in a
   * list, where opening a payment sheet over somebody's reservations unbidden would
   * be the screen deciding what they came to do.
   */
  autoOpen?: boolean;
  /** The card went through. «Submitted», not «paid» — the webhook decides that. */
  onSubmitted?: () => void;
  /**
   * The SERVER now says this booking is paid.
   *
   * Fired once, after the webhook has landed and been observed — never on the
   * strength of the card going through. A screen holding its own copy of the
   * booking uses this to stop calling it unpaid.
   */
  onPaid?: () => void;
}

/**
 * How long to keep asking, and how often.
 *
 * The webhook is usually there within a second or two; the wait exists for the
 * occasions it is not — a cold API, a Stripe retry after a 409. Bounded because an
 * unbounded poll is a page that never settles and a tab that never sleeps, and
 * because the honest thing to say after half a minute is «it is taking longer»
 * rather than nothing at all.
 */
const POLL_INTERVAL_MS = 2_000;
const POLL_ATTEMPTS = 15;

/**
 * HOW THE HELD COURT IS COUNTED DOWN (#607).
 *
 * «When they hit book the court is held for 15 minutes. During that time they have
 * to complete payment or they lose the booking» — so the countdown is not a
 * decoration beside the button, it is the thing the member is acting on. Concert
 * tickets, not a receipt.
 *
 * ── A SERVER-MEASURED DURATION IS NOT ITSELF A CLOCK ────────────────
 *
 * The server says «nine minutes left» and that is the right thing to sync, because
 * it carries no assumption about the device's clock being right. But it is a
 * MEASUREMENT AT A MOMENT, and the obvious next step — subtract 1000 ms per
 * `setInterval` callback — is wrong in the commonest case there is.
 *
 * A BACKGROUNDED TAB DOES NOT GET ONE CALLBACK PER SECOND. Browsers throttle
 * intervals in hidden tabs and stop them on a sleeping phone, so a tab asleep for
 * eight minutes wakes, receives ONE callback, and shows a value eight minutes too
 * generous — «Betal nå · 8:00» for a court whose deadline has gone. The server
 * still refuses to charge it, so no money moves on a lie; the member is simply
 * being asked to trust a number that is not true.
 *
 * SO THE REMAINING TIME IS DERIVED, NEVER ACCUMULATED: what the server last said,
 * minus how much LOCAL time has elapsed since it said it. Only an elapsed-time
 * difference is used, so a device clock that is minutes off cancels out entirely —
 * and the interval's only job is to make the component re-render, not to be the
 * arithmetic. A sleeping tab wakes up correct before any network call.
 *
 * AND ZERO IS NOT A CONCLUSION. Reaching 0:00 means «ask», not «it is gone» — the
 * sweep may not have run, the payment may have landed in the last second, and only
 * the server knows. So the component asks, and says what it is told.
 */
const TICK_MS = 1_000;

/**
 * What the server last said, and when this client heard it.
 *
 * `atMs` is a LOCAL `Date.now()` and is only ever used in a subtraction against
 * another local `Date.now()`. That is what makes the device's clock offset
 * irrelevant — the pair measures elapsed time, not wall-clock agreement.
 */
interface HoldSync {
  readonly remainingMs: number;
  readonly atMs: number;
}

/** The derived remaining time, or null when no hold is known. Never negative. */
function remainingFrom(sync: HoldSync | null, nowMs: number): number | null {
  if (sync === null) return null;
  return Math.max(0, sync.remainingMs - (nowMs - sync.atMs));
}

/** `m:ss`, which is how a countdown is read. Never negative. */
function clock(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export function PayForBooking({
  booking,
  subject = 'booking',
  summary,
  expiresInMs,
  autoOpen = false,
  onSubmitted,
  onPaid,
}: PayForBookingProps) {
  const t = useT();
  const [open, setOpen] = useState(autoOpen);
  /**
   * What the server last said about the hold, and when we heard it.
   *
   * `null` means «no hold known» — either not asked yet, or asked and told there
   * is none (a free booking, or a booking older than the rule). That is different
   * from «zero left», which is why this is not a bare number.
   */
  const [sync, setSync] = useState<HoldSync | null>(
    expiresInMs === undefined ? null : { remainingMs: expiresInMs, atMs: Date.now() },
  );
  /** Re-render ticker. It moves the CLOCK FORWARD, it does not do the arithmetic. */
  const [nowMs, setNowMs] = useState(() => Date.now());
  /** The server said the booking is gone. The court was released. */
  const [expired, setExpired] = useState(false);
  /**
   * The payment route is not there — no provider on this deployment (#1126).
   * The court is still held and unpaid; what is missing is a way to pay, which
   * is a different sentence from «the court was released», and not a red one.
   */
  const [unavailable, setUnavailable] = useState(false);
  const leftMs = remainingFrom(sync, nowMs);
  // 'idle' → nothing submitted. 'waiting' → card through, asking the server.
  // 'paid' → the server said so. 'slow' → it did not, within the bound.
  const [wait, setWait] = useState<'idle' | 'waiting' | 'paid' | 'slow'>('idle');
  const bookingId = paymentDueFor(booking) ? booking.id : null;
  // Read in the effect without making it a dependency: a parent that re-renders
  // with a new closure must not restart the poll.
  const paidRef = useRef(onPaid);
  paidRef.current = onPaid;

  // ── THE TICKER, AND THE ONE READ THAT SEEDS IT ──────────────────────
  //
  // Local ticking, server-measured start. The component never derives the
  // remaining time from a timestamp and the device's own clock.
  useEffect(() => {
    if (bookingId === null || expired || unavailable) return undefined;
    // THE SCREEN DID NOT KNOW. A reservations list renders from a projection that
    // carries no deadline, so this is where it is fetched — once, per unpaid
    // booking, which is a cheap read and never more than a member has bookings.
    let cancelled = false;
    if (sync === null) {
      void readBookingPaymentStatus(bookingId, subject).then((res) => {
        if (cancelled) return;
        if (res.state === 'gone') {
          setExpired(true);
          return;
        }
        // THE ROUTE IS NOT THERE (#1126): no provider, nothing to pay through,
        // and nothing to count down to. Not «gone» — the court is still held.
        if (res.state === 'not_configured') {
          setUnavailable(true);
          return;
        }
        // THE SERVER SAYS PAID (tester report, 2026-10-26). The row's copy of the
        // booking can be stale — the list is loaded once, and a payment made in
        // the booking flow never reached it — so it still offered «Betal nå» and
        // a running countdown for a court the server knew was paid. The status
        // read is the server's word: stop offering payment and report it.
        if (res.state === 'ok' && res.paymentStatus === 'paid') {
          setWait('paid');
          paidRef.current?.();
          return;
        }
        if (res.state === 'ok' && res.expiresInMs !== undefined) {
          setSync({ remainingMs: res.expiresInMs, atMs: Date.now() });
        }
      });
    }
    return () => {
      cancelled = true;
    };
    // `sync` is deliberately NOT a dependency: this effect seeds it, and depending
    // on what it sets would re-run on every tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId, subject, expired, unavailable]);

  useEffect(() => {
    if (sync === null || expired || wait === 'paid') return undefined;
    // ONE JOB: move `nowMs` so the derivation is recomputed. It is deliberately not
    // `ms - TICK_MS`, because a callback is not a second — see the header.
    const timer = setInterval(() => setNowMs(Date.now()), TICK_MS);
    return () => clearInterval(timer);
  }, [sync, wait, expired]);

  // ── COMING BACK IS WHEN AUTHORITY MATTERS MOST ──────────────────────
  //
  // The derivation above is already right the instant a sleeping tab wakes, because
  // it measures elapsed time rather than counting callbacks. But «right about what
  // the server said eight minutes ago» is not the same as «right»: the sweep may
  // have released the court in the meantime. So returning to the page re-reads.
  useEffect(() => {
    if (bookingId === null || expired || unavailable || wait === 'paid') return undefined;
    const resync = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      setNowMs(Date.now());
      void readBookingPaymentStatus(bookingId, subject).then((res) => {
        if (res.state === 'gone') {
          setExpired(true);
          return;
        }
        if (res.state === 'not_configured') {
          setUnavailable(true);
          return;
        }
        // Same as on mount: a paid answer ends the offer, whenever it arrives.
        if (res.state === 'ok' && res.paymentStatus === 'paid') {
          setWait('paid');
          paidRef.current?.();
          return;
        }
        if (res.state === 'ok' && res.expiresInMs !== undefined) {
          setSync({ remainingMs: res.expiresInMs, atMs: Date.now() });
        }
      });
    };
    document.addEventListener('visibilitychange', resync);
    window.addEventListener('focus', resync);
    return () => {
      document.removeEventListener('visibilitychange', resync);
      window.removeEventListener('focus', resync);
    };
  }, [bookingId, subject, expired, unavailable, wait]);

  // ── ZERO IS A QUESTION, NOT AN ANSWER ───────────────────────────────
  //
  // Reaching 0:00 locally proves only that this tab counted to zero. The sweep
  // runs on its own cadence, the payment may have landed in the last second, and
  // the booking may well still be there. So ask — and believe the answer.
  useEffect(() => {
    if (leftMs === null || leftMs > 0 || expired || unavailable || wait === 'paid') {
      return undefined;
    }
    if (bookingId === null) return undefined;
    let cancelled = false;
    const ask = async () => {
      const res = await readBookingPaymentStatus(bookingId, subject);
      if (cancelled) return;
      if (res.state === 'gone') {
        setExpired(true);
        return;
      }
      if (res.state === 'not_configured') {
        setUnavailable(true);
        return;
      }
      if (res.state === 'ok' && res.paymentStatus === 'paid') {
        setWait('paid');
        paidRef.current?.();
        return;
      }
      // Still there, still unpaid: the sweep has not reached it. Ask again rather
      // than declaring a court lost that the member could still pay for.
      timer = setTimeout(() => void ask(), POLL_INTERVAL_MS * 2);
    };
    let timer: ReturnType<typeof setTimeout> = setTimeout(() => void ask(), 0);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [leftMs, expired, unavailable, wait, bookingId, subject]);

  // ── ASKING WHETHER THE WEBHOOK LANDED ───────────────────────────────
  //
  // `confirmPayment` succeeding means the PROVIDER accepted the card. The booking
  // becomes paid when the signed webhook arrives — on another connection, moments
  // later, telling this page nothing. So the page asks.
  //
  // This is NOT the browser deciding money moved: every answer is the server's
  // `paymentStatus`, which only the webhook path writes. The page is a reader.
  useEffect(() => {
    if (wait !== 'waiting' || bookingId === null) return undefined;
    let cancelled = false;
    let attempts = 0;
    let timer: ReturnType<typeof setTimeout>;

    const ask = async () => {
      attempts += 1;
      const res = await readBookingPaymentStatus(bookingId, subject);
      if (cancelled) return;
      if (res.state === 'gone') {
        // The hold lapsed while the card was being confirmed. Rare, and it must
        // not be reported as «still confirming» for ever.
        setExpired(true);
        return;
      }
      if (res.state === 'ok' && res.paymentStatus === 'paid') {
        setWait('paid');
        paidRef.current?.();
        return;
      }
      // RE-SEEDED FROM THE SERVER on every read: a backgrounded tab's interval is
      // throttled, a sleeping phone's stops, and the local tick drifts behind the
      // real deadline. This is what puts it right.
      if (res.state === 'ok' && res.expiresInMs !== undefined) {
        setSync({ remainingMs: res.expiresInMs, atMs: Date.now() });
      }
      // An unreachable server has learned NOTHING — not «not paid» — so it is
      // treated exactly like a `pending` answer: wait, and ask again.
      if (attempts >= POLL_ATTEMPTS) {
        setWait('slow');
        return;
      }
      timer = setTimeout(() => void ask(), POLL_INTERVAL_MS);
    };

    timer = setTimeout(() => void ask(), POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [wait, bookingId, subject]);

  if (!paymentDueFor(booking)) return null;

  // ── THE COURT IS GONE, AND THE MEMBER IS TOLD ───────────────────────
  //
  // Not a silently vanishing row. «They lose the booking» is a thing that happens
  // TO somebody, and a product that does it without a sentence is one that looks
  // broken — the screen behind this will refresh the booking away on its own next
  // read, so this is the only moment there is to say what happened.
  if (expired) {
    return (
      <span className="text-[11px] font-semibold text-rose-700" data-testid="pay-for-booking-lost">
        {t('booking.pay.holdLost')}
      </span>
    );
  }

  // ── THERE IS NO WAY TO PAY HERE, AND THAT IS SAID AS WHAT IT IS (#1126) ──
  //
  // The route answered a bare 404: no provider on this deployment. Until this
  // existed the screen read that as the hold lapsing and told the member, in red,
  // that a court they still held had been released. «Not set up» is neutral, and
  // offers no «Betal nå» that would only open a sheet saying the same thing.
  if (unavailable) {
    return (
      <span className="text-[11px] opacity-80" data-testid="pay-for-booking-unavailable">
        {t('booking.pay.notConfigured')}
      </span>
    );
  }

  return (
    <>
      {/* HIDDEN ONCE THE CARD WENT THROUGH, because `paymentStatus` does not flip
          until the webhook lands — the server is still saying `pending`, and
          offering «betal nå» again would invite paying twice for what is already
          settled at the provider. */}
      {wait === 'idle' && (
        <span className="inline-flex items-center gap-2">
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="rounded-xl px-3 py-2 text-[12px] font-semibold"
            style={{ background: '#c2f02f', color: '#0f1611' }}
            data-testid="pay-for-booking"
          >
            {t('booking.pay.payNow')}
          </button>
          {/* THE COUNTDOWN, BESIDE THE ACTION IT BOUNDS. Shown only while a hold
              is actually known to be running — never as «0:00», which would read
              as «too late» at the exact moment the member still has time. The
              last minute is red, because that is when it matters. */}
          {leftMs !== null && leftMs > 0 && (
            <span
              className={`text-[11px] font-semibold tabular-nums ${
                leftMs <= 60_000 ? 'text-rose-700' : 'opacity-80'
              }`}
              data-testid="pay-for-booking-countdown"
            >
              {t('booking.pay.heldFor', { time: clock(leftMs) })}
            </span>
          )}
        </span>
      )}
      {wait === 'waiting' && (
        <span className="text-[11px] opacity-80" data-testid="pay-for-booking-submitted">
          {t('booking.pay.submittedShort')}
        </span>
      )}
      {wait === 'paid' && (
        <span className="text-[11px] font-semibold" data-testid="pay-for-booking-paid">
          {t('booking.pay.paid')}
        </span>
      )}
      {wait === 'slow' && (
        // NOT an error. The card went through; only the confirmation is late, and
        // telling somebody their payment failed when it did not is the worse lie.
        <span className="text-[11px] opacity-80" data-testid="pay-for-booking-slow">
          {t('booking.pay.stillConfirming')}
        </span>
      )}
      {open && (
        <StripePaymentSheet
          bookingId={booking.id}
          subject={subject}
          bookingSummary={summary}
          // THE SAME NUMBER, from the same source. A sheet with its own clock
          // would eventually show a different minute than the screen behind it.
          heldForMs={leftMs}
          // CLOSING LOSES NOTHING NOW: the booking stays pending with its court
          // held, and this button is still here.
          onCancel={() => setOpen(false)}
          // THE SERVER WON'T OPEN A PAYMENT BECAUSE IT IS ALREADY PAID: close the
          // sheet and report paid, rather than leave an error over a stale row.
          onAlreadyPaid={() => {
            setOpen(false);
            setWait('paid');
            paidRef.current?.();
          }}
          onSubmitted={() => {
            // THE SHEET CLOSES ITSELF. It used to stay mounted on its «submitted»
            // frame with a Close button nobody expected to have to press, over a
            // screen already saying the same thing — which read as a hung dialog,
            // and was reported as one. There is nothing left to do in it.
            setOpen(false);
            setWait('waiting');
            onSubmitted?.();
          }}
        />
      )}
    </>
  );
}
